import React from 'react';

/**
 * ErrorBoundary che intercetta SOLO errori di caricamento chunk (ChunkLoadError)
 * e ricarica automaticamente la pagina per ottenere i nuovi file dopo un deploy.
 * NON deve intercettare altri errori JavaScript generici.
 */
class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, isChunkError: false };
  }

  static getDerivedStateFromError(error) {
    // Check if it's STRICTLY a chunk loading error
    const isChunkError = 
      error.name === 'ChunkLoadError' ||
      (error.message?.includes('Loading chunk') && error.message?.includes('failed')) ||
      (error.message?.includes('Loading CSS chunk') && error.message?.includes('failed')) ||
      error.message?.includes('Failed to fetch dynamically imported module');
    
    // Only set hasError for chunk errors, let other errors propagate normally
    if (isChunkError) {
      return { hasError: true, error, isChunkError: true };
    }
    
    // For non-chunk errors, don't catch them - let them propagate
    return null;
  }

  componentDidCatch(error, errorInfo) {
    // Only handle chunk errors
    if (!this.state.isChunkError) {
      // Re-throw non-chunk errors
      throw error;
    }

    console.error('ChunkErrorBoundary caught CHUNK error:', error.message);
    console.log('🔄 Detected chunk loading error - attempting reload...');
    
    // Prevent infinite reload loop with stricter check
    const reloadCount = parseInt(sessionStorage.getItem('chunk_error_count') || '0');
    const lastReload = sessionStorage.getItem('chunk_error_reload');
    const now = Date.now();
    
    // Max 2 reloads in 60 seconds
    if (reloadCount >= 2 && lastReload && (now - parseInt(lastReload)) < 60000) {
      console.error('Too many chunk errors - stopping reload loop');
      // Reset after showing error
      sessionStorage.removeItem('chunk_error_count');
      sessionStorage.removeItem('chunk_error_reload');
      return;
    }
    
    // Only reload if we haven't reloaded in the last 30 seconds
    if (!lastReload || (now - parseInt(lastReload)) > 30000) {
      sessionStorage.setItem('chunk_error_reload', now.toString());
      sessionStorage.setItem('chunk_error_count', (reloadCount + 1).toString());
      
      // Clear any cached data
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => caches.delete(name));
        });
      }
      
      // Force reload from server
      setTimeout(() => {
        window.location.reload(true);
      }, 500);
    }
  }

  render() {
    if (this.state.hasError && this.state.isChunkError) {
      // Fallback UI only for chunk errors
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
          <div className="text-center p-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-700 mb-2">
              Aggiornamento in corso...
            </h2>
            <p className="text-gray-500">
              È disponibile una nuova versione dell'app. Ricaricamento automatico...
            </p>
            <button 
              onClick={() => {
                sessionStorage.removeItem('chunk_error_count');
                sessionStorage.removeItem('chunk_error_reload');
                window.location.reload(true);
              }}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Ricarica manualmente
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
